// src/AddToCalendar.tsx
//
// „Přidat do kalendáře" (nice-to-have po redesignu, rozhodnuto 2. 10. 2026):
// nabídka u nadcházejícího závodu (karta na přehledu, přihláška) – soubor .ics
// (Apple Kalendář, Outlook, Android) a Google Kalendář. Obsah události
// skládá utils/calendar.ts (celodenní událost, připomínka den předem).
//
// Soubor .ics se stahuje jen v prohlížeči / PWA: v obalu aplikace LKS
// (WebView) stažení souboru nefunguje, zbývá Google Kalendář (nový obal
// odkaz předá systému, starý ho neumí otevřít – odkaz se zkopíruje).

import React, { useCallback, useMemo, useState } from "react";
import { Modal } from "./ui/ConfirmDialog";
import { useToast } from "./ui/Toast";
import {
  buildIcs,
  googleCalendarUrl,
  icsFileName,
  raceCalendarEvent,
} from "./utils/calendar";
import { copyToClipboard } from "./utils/clipboard";
import { toIsoDate } from "./utils/dates";
import { canOpenExternalLinks, isNativeApp } from "./utils/platform";
import { reportWarning } from "./utils/reportError";

/** Závod tak, jak ho mají obrazovky (datum jako text nebo Timestamp). */
export interface CalendarRaceSource {
  id: string;
  name?: string;
  date?: unknown;
  discipline?: string | null;
  shootingRange?: string;
  mapUrl?: string;
  /** false = závod jiného pořadatele (popis události neříká „Závod LKS"). */
  organizedByLKS?: boolean;
}

export interface AddToCalendar {
  /** Nabídka dává smysl: platné datum a závod ještě nebyl. */
  available: boolean;
  open: () => void;
  /** Dialog nabídky – vykreslit kdekoli v komponentě. */
  modal: React.ReactNode;
}

/** Dnešní datum "YYYY-MM-DD" v místním čase. */
const todayIso = (): string => toIsoDate(new Date());

export function useAddToCalendar(race: CalendarRaceSource): AddToCalendar {
  const { showToast } = useToast();
  const [isOpen, setOpen] = useState(false);
  const [env] = useState(() => {
    const canOpen = canOpenExternalLinks();
    return { canOpen, canDownload: canOpen && !isNativeApp() };
  });

  const event = useMemo(
    () =>
      raceCalendarEvent(
        {
          id: race.id,
          name: race.name,
          date: toIsoDate(race.date),
          discipline: race.discipline,
          shootingRange: race.shootingRange,
          mapUrl: race.mapUrl,
          organizedByLKS: race.organizedByLKS,
        },
        typeof window !== "undefined" ? window.location.origin : ""
      ),
    [
      race.id,
      race.name,
      race.date,
      race.discipline,
      race.shootingRange,
      race.mapUrl,
      race.organizedByLKS,
    ]
  );
  const googleUrl = useMemo(() => googleCalendarUrl(event), [event]);
  const available = googleUrl !== "" && event.date >= todayIso();

  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  // Zavřít až po kliknutí – odkaz odpojený z DOM by se neotevřel (jako Navigovat)
  const closeAfterClick = () => window.setTimeout(close, 0);

  const downloadIcs = () => {
    try {
      const content = buildIcs(event, new Date());
      const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = icsFileName(event);
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      reportWarning("AddToCalendar.ics", err);
      showToast("Soubor kalendáře se nepodařilo vytvořit.", "warning");
    }
    close();
  };

  const copyGoogle = async () => {
    const ok = await copyToClipboard(googleUrl);
    showToast(
      ok
        ? "Odkaz do Google Kalendáře zkopírován – vložte ho do prohlížeče v telefonu."
        : "Odkaz se nepodařilo zkopírovat.",
      ok ? "success" : "warning"
    );
    close();
  };

  const modal = available ? (
    <Modal open={isOpen} onClose={close} title="Přidat do kalendáře" size="sm">
      <p className="fw-semibold mb-1">{event.title}</p>
      <p className="text-body-secondary small mb-3">
        Celodenní událost.
        {env.canDownload
          ? ""
          : " Soubor pro Apple Kalendář nebo Outlook stáhnete na webu aplikace v prohlížeči."}
      </p>
      <div className="list-group">
        {env.canDownload && (
          <button
            type="button"
            className="list-group-item list-group-item-action lks-actionlist__item"
            onClick={downloadIcs}
          >
            <i className="bi bi-calendar-event lks-actionlist__icon" aria-hidden="true" />
            <span className="lks-actionlist__text">
              <span className="lks-actionlist__label">Apple Kalendář, Outlook, Android</span>
              <span className="lks-actionlist__desc">
                Soubor .ics s připomínkou den předem – otevře se v kalendáři
              </span>
            </span>
            <i className="bi bi-download lks-actionlist__chevron" aria-hidden="true" />
          </button>
        )}
        {env.canOpen ? (
          <a
            href={googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="list-group-item list-group-item-action lks-actionlist__item"
            onClick={closeAfterClick}
          >
            <i className="bi bi-google lks-actionlist__icon" aria-hidden="true" />
            <span className="lks-actionlist__text">
              <span className="lks-actionlist__label">Google Kalendář</span>
              <span className="lks-actionlist__desc">
                Připomínku nastaví Google podle vašeho kalendáře
              </span>
            </span>
            <i className="bi bi-box-arrow-up-right lks-actionlist__chevron" aria-hidden="true" />
          </a>
        ) : (
          <button
            type="button"
            className="list-group-item list-group-item-action lks-actionlist__item"
            onClick={copyGoogle}
          >
            <i className="bi bi-google lks-actionlist__icon" aria-hidden="true" />
            <span className="lks-actionlist__text">
              <span className="lks-actionlist__label">Google Kalendář – zkopírovat odkaz</span>
              <span className="lks-actionlist__desc">
                Tato verze aplikace LKS neumí otevírat odkazy mimo aplikaci.
              </span>
            </span>
            <i className="bi bi-clipboard lks-actionlist__chevron" aria-hidden="true" />
          </button>
        )}
      </div>
    </Modal>
  ) : null;

  return { available, open, modal };
}
